import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ArrowUp } from "lucide-react";

const FacebookIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

const InstagramIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" strokeWidth="3" />
  </svg>
);

const LinkedInIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect x="2" y="9" width="4" height="12" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const SOCIAL_LINKS = [
  { name: "instagram", icon: <InstagramIcon />, url: "https://instagram.com" },
];

const PRODUCT_LINKS = [
  { label: "Frozen Fruit Pulps & Purees", path: "/products?category=frozen-fruit-pulps-purees" },
  { label: "Frozen Fruit Slices & Dices", path: "/products?category=frozen-fruit-slices-dices-chunks" },
  { label: "Imported Fruits & Berries", path: "/products?category=imported-fruits-berries" },
  { label: "Frozen Vegetables & Pastes", path: "/products?category=frozen-vegetables-pastes" },
  { label: "Specialty Products", path: "/products?category=specialty-products" },
];

const QUICK_LINKS = [
  { label: "About Us", path: "/about" },
  { label: "Our Products", path: "/products" },
  { label: "Services", path: "/services" },
  { label: "Contact Us", path: "/contact" },
  { label: "FAQ", path: "/faq" },
  { label: "Privacy Policy", path: "/privacy" },
  { label: "Terms & Conditions", path: "/terms" },
];

export const Footer = () => {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer className="bg-[#005A80] text-white relative" data-testid="footer">
      {/* Top accent bar */}
      <div className="h-1 bg-[#2B7A1E]" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="mb-5">
              <div className="inline-block bg-white rounded-2xl px-4 py-2 shadow-sm">
                <img
                  src="/logo.webp"
                  alt="Shri Ram Frozen Foods"
                  className="h-16 w-auto object-contain"
                />
              </div>
            </div>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              Pune-based supplier of premium frozen fruits, pulps, purees, berries, and vegetables. Trusted by food businesses across India.
            </p>
            <div className="flex gap-3">
              {SOCIAL_LINKS.map(({ name, icon, url }) => (
                <a
                  key={name}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-${name}-link`}
                  aria-label={name.charAt(0).toUpperCase() + name.slice(1)}
                  className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#2B7A1E] transition-colors duration-200"
                >
                  {icon}
                </a>
              ))}
            </div>
          </div>

          {/* Product Categories */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider mb-5" style={{ fontFamily: 'Montserrat' }}>Products</h4>
            <ul className="space-y-2.5">
              {PRODUCT_LINKS.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-white/70 hover:text-white hover:pl-1 transition-all duration-200"
                    data-testid={`footer-product-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider mb-5" style={{ fontFamily: 'Montserrat' }}>Quick Links</h4>
            <ul className="space-y-2.5">
              {QUICK_LINKS.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-white/70 hover:text-white hover:pl-1 transition-all duration-200"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider mb-5" style={{ fontFamily: 'Montserrat' }}>Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex gap-3">
                <Phone size={16} className="text-green-300 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-white/70">
                  <a href="tel:+918308203097" className="hover:text-white block">+91 83082 03097 (Mr. Kunal Zende)</a>
                  <a href="tel:+919680249797" className="hover:text-white block">+91 96802 49797 (Mr. Umesh Zende)</a>
                </div>
              </li>
              <li className="flex gap-3">
                <Mail size={16} className="text-green-300 mt-0.5 flex-shrink-0" />
                <a href="mailto:shriramfoodsprocessing@gmail.com" className="text-sm text-white/70 hover:text-white">
                  shriramfoodsprocessing@gmail.com
                </a>
              </li>
              <li className="flex gap-3">
                <MapPin size={16} className="text-green-300 mt-0.5 flex-shrink-0" />
                <a
                  href="https://maps.app.goo.gl/pqWf4gjZbcrvXrgG9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-white/70 hover:text-white transition-colors"
                >
                  Saswad, Pune, Maharashtra 412301
                </a>
              </li>
              <li className="flex gap-3">
                <Clock size={16} className="text-green-300 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-white/70">
                  <span className="block">All Days: 9:00 AM – 6:00 PM</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-white/50">
            &copy; {new Date().getFullYear()} Shri Ram Frozen Foods. All Rights Reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-xs text-white/50 hover:text-white/80 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-xs text-white/50 hover:text-white/80 transition-colors">Terms & Conditions</Link>
          </div>
        </div>

        {/* Elanswer Credit */}
        <div className="mt-4 text-center">
          <p className="text-xs text-white/30">
            Designed &amp; Developed by{" "}
            <a
              href="https://elanswer.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/50 hover:text-white transition-colors duration-200"
            >
              Elanswer
            </a>
          </p>
        </div>
      </div>

      {/* Scroll to top */}
      <button
        onClick={scrollToTop}
        className="absolute right-6 -top-5 w-10 h-10 rounded-full bg-[#2B7A1E] text-white flex items-center justify-center shadow-lg hover:bg-[#236618] transition-colors"
        data-testid="scroll-to-top-btn"
        aria-label="Scroll to top"
      >
        <ArrowUp size={18} />
      </button>
    </footer>
  );
};
